// apps/web/src/components/forms/PasswordStrengthIndicator.tsx
//
// Plan 04-12b Task 1 — visual password strength indicator.
//
// Composes with react-hook-form: the component does NOT own the password
// state. Callers pass the password string (typically derived via useWatch on
// the form control) and we render a 4-segment bar + textual label.
//
// PER STACK.md NOTE: react-hook-form's `watch()` doesn't re-render reliably
// under React 19 + Compiler; the parent uses `useWatch()` and passes the value
// down. This component is a pure presentation primitive.
//
// CONTEXT D-04: non-blocking — even score 0 is submittable at the form level.
// The bar is purely educational.

import type * as React from "react";
import { scorePassword } from "@/lib/zxcvbn-lite";
import { cn } from "@/lib/utils";

export interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

const SEGMENT_COLORS: Record<number, string> = {
  0: "bg-red-500",
  1: "bg-orange-500",
  2: "bg-yellow-500",
  3: "bg-lime-500",
  4: "bg-green-600",
};

export function PasswordStrengthIndicator(
  props: PasswordStrengthIndicatorProps,
): React.ReactElement | null {
  const { password, className } = props;
  if (password.length === 0) return null;

  const strength = scorePassword(password);

  return (
    <div
      data-testid="password-strength-indicator"
      data-strength-score={strength.score}
      className={cn("space-y-1", className)}
    >
      <div className="flex h-1.5 gap-1" aria-hidden="true">
        {[0, 1, 2, 3].map((segIdx) => (
          <div
            key={segIdx}
            className={cn(
              "flex-1 rounded-sm",
              segIdx <= strength.score - 1
                ? SEGMENT_COLORS[strength.score]
                : "bg-muted",
            )}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Strength: <span className="font-medium">{strength.label}</span>
      </p>
    </div>
  );
}
